import { useCallback, useEffect, useRef, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { useVault } from '@/hooks/useVault'
import type { AbrirAfipDirectoResult } from '@/services/afipDelegacionService'
import { PinInput } from './PinInput'
import { useClientesConClaveArca } from './useClientesConClaveArca'

interface Props {
  clienteId: string
  nombreCliente: string
  /** Si viene resuelto, el botón no consulta las claves de la organización. */
  tieneClave?: boolean
  /** Botón de solo ícono, para el renglón del listado. */
  compacto?: boolean
}

const CLIPBOARD_TTL_MS = 60_000
const AFIP_LOGIN_URL = 'https://auth.afip.gob.ar'

class PinInvalidoError extends Error {}

/**
 * Copia la clave cuando la promesa resuelve. Con `ClipboardItem` la escritura se inicia en esta
 * llamada, así que corre dentro del gesto del usuario. Sin soporte, escribe al resolver.
 */
const copiarClave = (clave: Promise<string>): Promise<void> => {
  if (typeof ClipboardItem === 'function' && navigator.clipboard?.write) {
    try {
      const blob = clave.then((c) => new Blob([c], { type: 'text/plain' }))
      return navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })])
    } catch {
      // El navegador no acepta una promesa en ClipboardItem.
    }
  }
  return clave.then((c) => navigator.clipboard.writeText(c))
}

export const AbrirAfipDirectoButton = ({ clienteId, nombreCliente, tieneClave, compacto }: Props) => {
  const { desbloqueado, verificarPin, abrirAfipDirecto } = useVault()
  const { data: clientesConClave } = useClientesConClaveArca(tieneClave === undefined)
  const [loading, setLoading] = useState(false)
  const [pinDialogOpen, setPinDialogOpen] = useState(false)
  const [pinError, setPinError] = useState(false)
  const clipboardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (clipboardTimeoutRef.current) clearTimeout(clipboardTimeoutRef.current)
    },
    [],
  )

  // Se llama sincrónico dentro del gesto: inicia el copiado y abre la pestaña antes de que
  // resuelva `resultado`. La pestaña va a ARCA si todo sale bien y se cierra si algo falla.
  const abrir = useCallback((resultado: Promise<AbrirAfipDirectoResult>) => {
    setLoading(true)
    const copiado = copiarClave(resultado.then((r) => r.clave))
    copiado.catch(() => {})
    const pestana = window.open('', '_blank')
    if (pestana) pestana.opener = null
    void (async () => {
      try {
        const { cuit } = await resultado
        await copiado
        if (pestana) pestana.location.href = AFIP_LOGIN_URL
        else window.open(AFIP_LOGIN_URL, '_blank', 'noopener')
        toast.success(
          `Clave de ARCA de ${nombreCliente} copiada (CUIT ${cuit}). En ARCA pegá esta clave, no la del estudio que sugiere Chrome. Se borra del portapapeles en 60 s.`,
        )
        clipboardTimeoutRef.current = setTimeout(() => {
          navigator.clipboard.writeText('').catch(() => {})
        }, CLIPBOARD_TTL_MS)
      } catch (error) {
        pestana?.close()
        if (error instanceof PinInvalidoError) {
          setPinError(true)
          toast.error('PIN inválido')
        } else {
          toast.error(error instanceof Error ? error.message : 'Error al abrir AFIP')
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [nombreCliente])

  const handleClick = useCallback(() => {
    if (desbloqueado) {
      abrir(abrirAfipDirecto(clienteId))
      return
    }
    setPinError(false)
    setPinDialogOpen(true)
  }, [desbloqueado, abrir, abrirAfipDirecto, clienteId])

  const handlePinComplete = useCallback(
    (pin: string) => {
      abrir(
        verificarPin(pin).then((ok) => {
          if (!ok) throw new PinInvalidoError()
          setPinError(false)
          setPinDialogOpen(false)
          return abrirAfipDirecto(clienteId)
        }),
      )
    },
    [verificarPin, abrir, abrirAfipDirecto, clienteId],
  )

  if (!(tieneClave ?? clientesConClave?.has(clienteId))) return null

  return (
    <>
      {compacto ? (
        <Button
          size="sm"
          variant="ghost"
          className="h-8 gap-1 px-2 text-xs font-semibold"
          onClick={handleClick}
          disabled={loading}
          title="Abrir ARCA"
          aria-label="Abrir ARCA"
        >
          ARCA
          <ExternalLink className="h-3 w-3" />
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={handleClick} disabled={loading}>
          <ExternalLink />
          Abrir AFIP
        </Button>
      )}

      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ingresá tu PIN</DialogTitle>
            <DialogDescription>
              Acceso directo a AFIP — {nombreCliente}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <PinInput onComplete={handlePinComplete} error={pinError} disabled={loading} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
